import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, DatePicker, Select, Button, Card, Row, Col, message, Table, Radio } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import apiClient from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import dayjs from 'dayjs';



const { RangePicker } = DatePicker;
const { TextArea } = Input;

// Constants
const SEARCH_TYPES = {
  EMPLOYEE: 'employee',
  LOCATION: 'location',
} as const;

type SearchType = typeof SEARCH_TYPES[keyof typeof SEARCH_TYPES];

interface CampaignAuditFormProps {
  onSubmit: (values: any) => void;
  loading?: boolean;
}

interface Company {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
}

interface Employee {
  id: number;
  full_name: string;
  employee_code?: string;
}

interface Asset {
  id: number;
  asset_code: string;
  name: string;
  location_name?: string;
}

const CampaignAuditForm: React.FC<CampaignAuditFormProps> = ({ onSubmit, loading = false }) => {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetCount, setAssetCount] = useState(0);
  const [searchType, setSearchType] = useState<SearchType>(SEARCH_TYPES.EMPLOYEE);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [loadingLocEmp, setLoadingLocEmp] = useState(false);
  const [assetList, setAssetList] = useState<Asset[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // Check user permissions
  const isSuperAdmin = user?.role === 'super_admin';
  const userCompanyId = user?.company_id;
  const currentCompanyName = companies.find(c => c.id === userCompanyId)?.name || '';

  // Helper functions
  const parseApiResponse = <T,>(data: any): T[] => {
    if (Array.isArray(data)) {
      return data;
    }
    if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  };

  const generateAuditName = (): string => {
    const currentYear = new Date().getFullYear();
    
    if (searchType === SEARCH_TYPES.EMPLOYEE && selectedEmployee) {
      return `[Kiểm kê định kỳ - ${selectedEmployee.full_name} - ${currentYear}]`;
    } else if (searchType === SEARCH_TYPES.LOCATION && selectedLocation) {
      return `[Tổng kiểm kê - ${selectedLocation.name} - ${currentYear}]`;
    }
    
    return '';
  };

  const updateAuditName = () => {
    const generatedName = generateAuditName();
    if (generatedName) {
      form.setFieldsValue({ name: generatedName });
    }
  };

  // Computed values
  const canSubmit = useMemo(() => {
    return assetCount > 0 && !loadingAssets;
  }, [assetCount, loadingAssets]);

  // API functions
  const loadCompanies = async () => {
    setLoadingCompany(true);
    try {
      const data = await apiClient.getCompanies();
      const companiesArr = parseApiResponse<Company>(data);
      setCompanies(companiesArr);
    } catch (error) {
      message.error('Lỗi khi tải danh sách công ty');
      message.error('Lỗi khi tải danh sách công ty');
    } finally {
      setLoadingCompany(false);
    }
  };

  const loadLocationsAndEmployees = async (companyId: number) => {
    setLoadingLocEmp(true);
    try {
      const [locs, emps] = await Promise.all([
        apiClient.getLocations({ company_id: companyId }),
        apiClient.getEmployees({ company_id: companyId })
      ]);
      
      const locArr = parseApiResponse<Location>(locs);
      const empArr = parseApiResponse<Employee>(emps);
      
      setLocations(locArr);
      setEmployees(empArr);
    } catch (error) {
      message.error('Lỗi khi tải danh sách địa điểm và nhân viên');
      message.error('Lỗi khi tải danh sách vị trí và nhân viên');
    } finally {
      setLoadingLocEmp(false);
    }
  };

  const loadAssets = async () => {
    const values = form.getFieldsValue();
    if (!values.company_id || (!values.location_id && !values.assigned_to_employee_id)) {
      return;
    }
    
    setLoadingAssets(true);
    try {
      const response = await apiClient.post('/inventory-audit/load-assets', {
        company_id: values.company_id,
        location_id: values.location_id,
        assigned_to_employee_id: values.assigned_to_employee_id,
        start_date: values.date_range?.[0]?.format('YYYY-MM-DD'),
        end_date: values.date_range?.[1]?.format('YYYY-MM-DD'),
      }) as any;
      
      setAssetCount(response.asset_count || 0);
      setAssetList(response.assets || []);
      
      if (response.asset_count > 0) {
        message.success(`Tìm thấy ${response.asset_count} tài sản phù hợp`);
      }
    } catch (error: any) {
      message.error('Lỗi khi tải danh sách tài sản');
      message.error(`Lỗi khi tải tài sản: ${error.response?.data?.message || error.message}`);
      setAssetCount(0);
      setAssetList([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Event handlers
  const handleCompanyChange = async (companyId: number) => {
    form.setFieldsValue({ 
      location_id: undefined, 
      assigned_to_employee_id: undefined 
    });
    setSelectedEmployee(null);
    setSelectedLocation(null);
    await loadLocationsAndEmployees(companyId);
  };

  const handleSearchTypeChange = (value: SearchType) => {
    setSearchType(value);
    setSelectedEmployee(null);
    setSelectedLocation(null);
    form.setFieldsValue({ 
      location_id: undefined, 
      assigned_to_employee_id: undefined,
      name: ''
    });
  };

  const handleEmployeeChange = (value: number) => {
    const employee = employees.find(emp => emp.id === value);
    setSelectedEmployee(employee || null);
  };

  const handleLocationChange = (value: number) => {
    const location = locations.find(loc => loc.id === value);
    setSelectedLocation(location || null);
  };

  const handleSubmit = async (values: any) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const submitData = {
      ...values,
      start_date: values.date_range?.[0]?.format('YYYY-MM-DD'),
      end_date: values.date_range?.[1]?.format('YYYY-MM-DD'),
      status: 'pending_setup',
      created_by_user_id: user.id,
    };
    delete submitData.date_range;
    onSubmit(submitData);
  };

  // Effects
  useEffect(() => {
    loadCompanies();
  }, []);

  // Set default company for non-super admin users
  useEffect(() => {
    if (!isSuperAdmin && userCompanyId && companies.length > 0) {
      form.setFieldsValue({ company_id: userCompanyId });
      loadLocationsAndEmployees(userCompanyId);
    }
  }, [isSuperAdmin, userCompanyId, companies, form]);

  useEffect(() => {
    updateAuditName();
    if (selectedEmployee || selectedLocation) {
      loadAssets();
    }
  }, [selectedEmployee, selectedLocation, searchType]);

  // Table columns for assets
  const assetColumns = [
    { 
      title: 'Mã tài sản', 
      dataIndex: 'asset_code', 
      key: 'asset_code' 
    },
    { 
      title: 'Tên tài sản', 
      dataIndex: 'name', 
      key: 'name' 
    },
    { 
      title: 'Vị trí', 
      dataIndex: 'location_name', 
      key: 'location_name' 
    },
  ];

  return (
    <Card 
      title={
        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
          Tạo đợt kiểm kê định kỳ
        </div>
      } 
      style={{ marginBottom: 16 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          search_type: SEARCH_TYPES.EMPLOYEE,
          status: 'pending_setup',
          date_range: [dayjs(), dayjs().add(30, 'day')],
        }}
        style={{ padding: '8px 0' }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label="Tên đợt kiểm kê"
              rules={[{ required: true, message: 'Vui lòng nhập tên đợt kiểm kê' }]}
            >
              <Input 
                placeholder="Tên sẽ được tự động sinh" 
                disabled 
                style={{ backgroundColor: '#f5f5f5' }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="company_id"
              label="Công ty"
              rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}
            >
              <Select
                placeholder={isSuperAdmin ? "Chọn công ty" : currentCompanyName || "Chọn công ty"}
                loading={loadingCompany}
                onChange={handleCompanyChange}
                showSearch={isSuperAdmin}
                optionFilterProp="children"
                disabled={!isSuperAdmin}
                style={!isSuperAdmin ? { backgroundColor: '#f5f5f5' } : {}}
              >
                {isSuperAdmin ? (
                  companies.map(company => (
                  <Select.Option key={company.id} value={company.id}>
                    {company.name}
                  </Select.Option>
                  ))
                ) : (
                  userCompanyId && (
                    <Select.Option key={userCompanyId} value={userCompanyId}>
                      {currentCompanyName}
                    </Select.Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="date_range"
              label="Thời gian kiểm kê"
              rules={[{ required: true, message: 'Vui lòng chọn thời gian kiểm kê' }]}
            >
              <RangePicker 
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="search_type"
              label="Tìm theo"
            >
              <Radio.Group
                value={searchType}
                onChange={(e: any) => handleSearchTypeChange(e.target.value)}
              >
                <Radio value={SEARCH_TYPES.EMPLOYEE}>Nhân viên</Radio>
                <Radio value={SEARCH_TYPES.LOCATION}>Vị trí</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col span={12}>
            {searchType === SEARCH_TYPES.LOCATION ? (
              <Form.Item
                name="location_id"
                label="Vị trí"
                rules={[{ required: true, message: 'Vui lòng chọn vị trí' }]}
              >
                <Select
                  placeholder="Chọn vị trí"
                  loading={loadingLocEmp}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleLocationChange}
                >
                  {locations.map(location => (
                    <Select.Option key={location.id} value={location.id}>
                      {location.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Form.Item
                name="assigned_to_employee_id"
                label="Nhân viên"
                rules={[{ required: true, message: 'Vui lòng chọn nhân viên' }]}
              >
                <Select
                  placeholder="Chọn nhân viên"
                  loading={loadingLocEmp}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleEmployeeChange}
                >
                  {employees.map(employee => (
                    <Select.Option key={employee.id} value={employee.id}>
                      {employee.full_name} {employee.employee_code ? `(${employee.employee_code})` : ''}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="description"
              label="Mô tả"
            >
              <TextArea 
                rows={3} 
                placeholder="Mô tả chi tiết về đợt kiểm kê..."
              />
            </Form.Item>
          </Col>
        </Row>

        {(assetCount > 0 || loadingAssets) && (
          <>
            <Row>
              <Col span={24}>
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: loadingAssets ? '#e6f7ff' : '#f6ffed', 
                  border: `1px solid ${loadingAssets ? '#91d5ff' : '#b7eb8f'}`,
                  borderRadius: '8px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', color: loadingAssets ? '#1890ff' : '#52c41a' }}>
                      {loadingAssets ? '⏳' : '✅'}
                    </span>
                    <strong style={{ fontSize: '16px', color: loadingAssets ? '#1890ff' : '#52c41a' }}>
                      {loadingAssets ? 'Đang tải tài sản...' : `Tìm thấy ${assetCount} tài sản phù hợp`}
                    </strong>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Table
                  dataSource={assetList}
                  columns={assetColumns}
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 10 }}
                  style={{ marginBottom: 16 }}
                />
              </Col>
            </Row>
          </>
        )}

        <Row>
          <Col span={24}>
            <Form.Item style={{ marginBottom: 0, textAlign: 'center' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<PlusOutlined />}
                disabled={!canSubmit}
                size="large"
                style={{ 
                  minWidth: '200px',
                  height: '40px',
                  fontSize: '16px'
                }}
              >
                Tạo đợt kiểm kê
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default CampaignAuditForm; 